// src/pages/plantation/standar-per-umur/StandarPerUmur.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, set, remove, push } from "firebase/database";
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    formatText
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper function untuk mengonversi string ke number dengan handling koma
const parseNumericValue = (value) => {
    if (!value || value.toString().trim() === '') return '';
    
    const strValue = value.toString().trim();
    
    // Ganti koma dengan titik untuk parsing number
    const normalizedValue = strValue.replace(',', '.');
    
    // Coba parse sebagai float
    const numValue = parseFloat(normalizedValue);
    
    // Jika bukan number, return string asli
    if (isNaN(numValue)) return strValue;
    
    // TRUNCATE ke 4 angka di belakang koma (bukan round)
    // Kalikan dengan 10000, ambil bagian integer, lalu bagi dengan 10000
    const truncatedValue = Math.floor(numValue * 10000) / 10000;
    
    // Konversi ke string dengan format yang benar (gunakan koma sebagai desimal)
    return truncatedValue.toString().replace('.', ',');
};

// Helper function untuk menampilkan nilai yang sudah dibulatkan
const displayRoundedValue = (value) => {
    if (!value || value.toString().trim() === '') return '';
    
    const strValue = value.toString().trim();
    const normalizedValue = strValue.replace(',', '.');
    const numValue = parseFloat(normalizedValue);
    
    if (isNaN(numValue)) return strValue;
    
    // Round ke bilangan bulat terdekat
    return Math.round(numValue).toString();
};

// Helper function untuk mem-parse index menjadi number
const parseIndexValue = (value) => {
    if (!value || value.toString().trim() === '') return '';
    
    const strValue = value.toString().trim();
    
    // Coba parse sebagai integer
    const numValue = parseInt(strValue, 10);
    
    // Jika bukan number, coba parse sebagai float lalu convert ke integer
    if (isNaN(numValue)) {
        const floatValue = parseFloat(strValue.replace(',', '.'));
        if (!isNaN(floatValue)) {
            return Math.round(floatValue).toString();
        }
        return '';
    }
    
    return numValue.toString();
};

function StandarPerUmur() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [isAddIconRotated, setIsAddIconRotated] = useState(false);
    
    // State untuk data baru
    const [newData, setNewData] = useState({
        index: '',
        umur: '',
        jenis_bibit: '',
        kelas_bibit: '',
        setara_urea: '',
        k_per_n: '',
        setara_k: '',
        setara_n: '',
        setara_k2so4: '',
        standar_sbt: ''
    });

    // Fungsi untuk mengecek apakah data dengan index yang sama sudah ada
    const isDataExists = (indexToCheck) => {
        if (!indexToCheck || !originalDataList) return false;
        
        const indexNum = parseInt(indexToCheck);
        if (isNaN(indexNum)) return false;
        
        return originalDataList.some(item => {
            const itemIndex = parseInt(item.index);
            return !isNaN(itemIndex) && itemIndex === indexNum;
        });
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data
                await fetchMainData();
                
            } catch (error) {
                console.error("Error fetching all data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

const fetchMainData = async () => {
    try {
        const standarUmurRef = ref(database, "db_plantation/tb_standar_per_umur");
        const standarUmurSnapshot = await onValuePromise(standarUmurRef);
        const standarUmurData = standarUmurSnapshot.val();
        const loadedData = [];
        let latestUpdateTime = null;
        
        if (standarUmurData) {
            Object.keys(standarUmurData).forEach(key => {
                const record = standarUmurData[key];
                loadedData.push({ 
                    firebaseKey: key,
                    id_standar_per_umur: key,
                    index: record.index || '',
                    umur: record.umur || '',
                    jenis_bibit: record.jenis_bibit || '',
                    kelas_bibit: record.kelas_bibit || '',
                    setara_urea: record.setara_urea || '',
                    k_per_n: record.k_per_n || '',
                    setara_k: record.setara_k || '',
                    setara_n: record.setara_n || '',
                    setara_k2so4: record.setara_k2so4 || '',
                    standar_sbt: record.standar_sbt || '',
                    last_updated: record.last_updated || null
                });
                
                if (record.last_updated) {
                    const updateTime = new Date(record.last_updated);
                    if (!latestUpdateTime || updateTime > latestUpdateTime) {
                        latestUpdateTime = updateTime;
                    }
                }
            });
            
            if (latestUpdateTime) {
                setLastUpdated(latestUpdateTime);
            }
        }

        setOriginalDataList(loadedData);
        setDataList(loadedData);

    } catch (error) {
        console.error("Error fetching main data:", error);
        throw error;
    }
};

    // Effect untuk melakukan pencarian saat searchQuery berubah
useEffect(() => {
    if (searchQuery) {
        const filtered = originalDataList.filter(item => 
            item.index.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.umur.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.jenis_bibit.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.kelas_bibit.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.setara_urea.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.k_per_n.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.setara_k.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.setara_n.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.setara_k2so4.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.standar_sbt.toLowerCase().includes(searchQuery.toLowerCase())
        );
        
        setDataList(filtered);
    } else {
        
        // Gunakan original tanpa sorting:
        setDataList([...originalDataList]);
    }
    setPage(0);
}, [searchQuery, originalDataList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) {
        setSnackbar({
            open: true,
            message: "Tidak ada file yang dipilih.",
            severity: "warning",
        });
        return;
    }

    setImportLoading(true);
    setSnackbar({
        open: true,
        message: "Memulai proses import...",
        severity: "info",
    });

    try {
        const reader = new FileReader();
        
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Baca semua data dari Excel
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                // =============== DEBUGGING LENGKAP ===============
                console.log("========== DEBUG EXCEL STRUCTURE ==========");
                console.log("Total rows in Excel file:", jsonData.length);
                
                // Cari baris data mulai
                let dataStartRowIndex = -1;
                
                // Cari baris pertama yang memiliki angka di Kolom A (Index)
                for (let i = 0; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (row && row.length > 0 && row[0] && !isNaN(parseInt(row[0]))) {
                        dataStartRowIndex = i;
                        console.log(`Found data start at row ${i + 1}:`, row);
                        break;
                    }
                }
                
                if (dataStartRowIndex === -1) {
                    // Fallback: mulai dari baris 5 (indeks 4) berdasarkan log
                    dataStartRowIndex = 4; // Baris 5 Excel
                    console.log(`Using fallback start row: ${dataStartRowIndex} (Row ${dataStartRowIndex + 1})`);
                }
                
                const dataRows = jsonData.slice(dataStartRowIndex);
                console.log(`Processing ${dataRows.length} data rows starting from row ${dataStartRowIndex + 1}`);
                
                // Tampilkan beberapa baris pertama untuk verifikasi
                console.log("\n=== FIRST 5 DATA ROWS FOR VERIFICATION ===");
                for (let i = 0; i < Math.min(5, dataRows.length); i++) {
                    const row = dataRows[i];
                    console.log(`Row ${dataStartRowIndex + i + 1}:`, {
                        'Kolom A (Index)': row[0],
                        'Kolom B (Umur)': row[1],
                        'Kolom C (Jenis)': row[2],
                        'Kolom D (Kelas)': row[3],
                        'Kolom E (Setara Urea)': row[4],
                        'Kolom F (K/N)': row[5],
                        'Kolom G (Setara K)': row[6],
                        'Kolom H (Setara N)': row[7],
                        'Kolom I (K2SO4)': row[8],
                        'Kolom J (Standar SBT)': row[9]
                    });
                }
                console.log("==========================================\n");
                // =============== AKHIR DEBUGGING ===============
                
                if (dataRows.length === 0) {
                    throw new Error("Tidak ditemukan data untuk diimport. Periksa struktur file Excel.");
                }

                // Path database
                const dbPath = "db_plantation/tb_standar_per_umur";
                const tbRef = ref(database, dbPath);

                // Ambil data yang sudah ada
                const snapshot = await onValuePromise(tbRef);
                const existingData = snapshot.exists() ? snapshot.val() : {};

                let importedCount = 0;
                let updatedCount = 0;
                let ignoredCount = 0;
                const errors = [];

                console.log("\n=== PROCESSING DATA ROWS ===");
                
                for (let i = 0; i < dataRows.length; i++) {
                    const row = dataRows[i];
                    const excelRowNumber = dataStartRowIndex + i + 1;
                    
                    try {
                        // Skip baris kosong
                        if (!row || row.length === 0) {
                            console.log(`[${excelRowNumber}] Baris kosong - diabaikan`);
                            ignoredCount++;
                            continue;
                        }
                        
                        // PERBAIKAN MAPPING KOLOM DI SINI:
                        const index = parseIndexValue(row[0]) || ''; // Kolom A: Index
                        const umur = row[1]?.toString().trim() || ''; // Kolom B: Umur
                        const jenisBibit = row[2]?.toString().trim() || ''; // Kolom C: Jenis Bibit
                        const kelasBibit = row[3]?.toString().trim() || ''; // Kolom D: Kelas Bibit
                        const setaraUrea = parseNumericValue(row[4]) || ''; // Kolom E: Setara Urea
                        const kPerN = parseNumericValue(row[5]) || ''; // Kolom F: K/N
                        const setaraK = parseNumericValue(row[6]) || ''; // Kolom G: Setara K
                        const setaraN = parseNumericValue(row[7]) || ''; // Kolom H: Setara N
                        const setaraK2SO4 = parseNumericValue(row[8]) || ''; // Kolom I: Setara K2SO4
                        const standarSBT = parseNumericValue(row[9]) || ''; // Kolom J: Standar SBT
                        
                        console.log(`[${excelRowNumber}] Parsed values:`, {
                            index, umur, jenisBibit, kelasBibit,
                            setaraUrea, kPerN, setaraK, setaraN,
                            setaraK2SO4, standarSBT
                        });

                        // Validasi: index wajib ada
                        if (!index || index.toString().trim() === '') {
                            console.log(`[${excelRowNumber}] Index kosong - diabaikan`);
                            ignoredCount++;
                            continue;
                        }
                        
                        // Validasi: index harus berupa number
                        const indexNum = parseInt(index);
                        if (isNaN(indexNum)) {
                            console.log(`[${excelRowNumber}] Index bukan angka (${index}) - diabaikan`);
                            ignoredCount++;
                            continue;
                        }
                        
                        // Validasi: umur wajib ada
                        if (!umur || umur.trim() === '') {
                            console.log(`[${excelRowNumber}] Umur kosong - diabaikan`);
                            ignoredCount++;
                            continue;
                        }

                        // Persiapkan data untuk disimpan
                        const dataToSave = {
                            index: index,
                            umur: umur,
                            jenis_bibit: jenisBibit,
                            kelas_bibit: kelasBibit,
                            setara_urea: setaraUrea,
                            k_per_n: kPerN,
                            setara_k: setaraK,
                            setara_n: setaraN,
                            setara_k2so4: setaraK2SO4,
                            standar_sbt: standarSBT,
                            last_updated: new Date().toISOString()
                        };

                        console.log(`[${excelRowNumber}] Data to save:`, dataToSave);

                        // Cari apakah data dengan index yang sama sudah ada
                        const existingKey = Object.keys(existingData).find(key => {
                            const existingIndex = existingData[key].index?.toString().trim();
                            return existingIndex === index.toString().trim();
                        });

                        if (existingKey) {
                            // Update data yang sudah ada
                            const existingItemRef = ref(database, `${dbPath}/${existingKey}`);
                            const dataWithId = {
                                ...dataToSave,
                                id_standar_per_umur: existingKey
                            };
                            await set(existingItemRef, dataWithId);
                            updatedCount++;
                            console.log(`[${excelRowNumber}] Data updated: index ${index}`);
                        } else {
                            // Simpan sebagai data baru dengan key unik
                            const newRef = push(ref(database, dbPath));
                            const dataWithId = {
                                ...dataToSave,
                                id_standar_per_umur: newRef.key
                            };
                            await set(newRef, dataWithId);
                            importedCount++;
                            console.log(`[${excelRowNumber}] New data: index ${index}`);
                        }

                    } catch (error) {
                        console.error(`[${excelRowNumber}] Error processing row:`, error, "Row data:", row);
                        errors.push(`Error pada baris ${excelRowNumber}: ${error.message}`);
                        ignoredCount++;
                    }
                }

                // Refresh data setelah import
                console.log("\n=== REFRESHING DATA ===");
                await fetchMainData();

                // Tampilkan hasil import
                console.log("\n=== IMPORT SUMMARY ===");
                console.log(`Imported: ${importedCount}`);
                console.log(`Updated: ${updatedCount}`);
                console.log(`Ignored: ${ignoredCount}`);
                console.log(`Errors: ${errors.length}`);
                
                let message = `Import selesai! ${importedCount} data baru, ${updatedCount} data diperbarui.`;
                let severity = "success";
                
                if (ignoredCount > 0) {
                    message += ` ${ignoredCount} baris diabaikan (data kosong/tidak valid).`;
                    severity = "warning";
                }
                
                if (errors.length > 0) {
                    console.error("Import errors:", errors);
                    message += ` Terdapat ${errors.length} error.`;
                }

                setSnackbar({
                    open: true,
                    message: message,
                    severity: severity,
                });

                console.log("Import process completed successfully");

            } catch (error) {
                console.error("Gagal mengimpor Excel:", error);
                setSnackbar({
                    open: true,
                    message: `Gagal membaca file Excel: ${error.message}`,
                    severity: "error",
                });
            } finally {
                setImportLoading(false);
            }
        };

        reader.onerror = (error) => {
            console.error("Error reading file:", error);
            setSnackbar({
                open: true,
                message: "Gagal membaca file.",
                severity: "error",
            });
            setImportLoading(false);
        };

        reader.readAsArrayBuffer(file);

    } catch (error) {
        console.error("Error in import process:", error);
        setSnackbar({
            open: true,
            message: "Terjadi kesalahan saat memproses file.",
            severity: "error",
        });
        setImportLoading(false);
    } finally {
        event.target.value = null;
    }
};

    const handleExport = () => {
        try {
            // Sort data by index sebelum export
            const sortedData = [...dataList].sort((a, b) => {
                const indexA = parseInt(a.index) || 0;
                const indexB = parseInt(b.index) || 0;
                return indexA - indexB;
            });
            
            // Siapkan data untuk export
            const exportData = sortedData.map(item => ({
                'Index': item.index,
                'Umur': item.umur,
                'Jenis Bibit': item.jenis_bibit,
                'Kelas Bibit': item.kelas_bibit,
                'Setara Urea': item.setara_urea,
                'K/N': item.k_per_n,
                'Setara K': item.setara_k,
                'Setara N': item.setara_n,
                'Setara K2SO4': item.setara_k2so4,
                'Standar SBT': item.standar_sbt
            }));

            // Buat worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);
            
            // Buat workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Standar Per Umur");
            
            // Simpan file
            XLSX.writeFile(wb, `Standar_Per_Umur_${new Date().toISOString().split('T')[0]}.xlsx`);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diexport.",
                severity: "success",
            });
            
        } catch (error) {
            console.error("Error exporting data:", error);
            setSnackbar({
                open: true,
                message: "Gagal mengexport data.",
                severity: "error",
            });
        }
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (row) => {
        setEditingRow(row.firebaseKey);
        setEditFormData({
            index: row.index,
            umur: row.umur,
            jenis_bibit: row.jenis_bibit,
            kelas_bibit: row.kelas_bibit,
            setara_urea: row.setara_urea,
            k_per_n: row.k_per_n,
            setara_k: row.setara_k,
            setara_n: row.setara_n,
            setara_k2so4: row.setara_k2so4,
            standar_sbt: row.standar_sbt
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: field === 'index' ? parseIndexValue(value) : 
                    ['setara_urea', 'k_per_n', 'setara_k', 'setara_n', 'setara_k2so4', 'standar_sbt'].includes(field) 
                    ? parseNumericValue(value) : value
        });
    };

const handleSaveEdit = async (firebaseKey) => {
    try {
        const dataRef = ref(database, `db_plantation/tb_standar_per_umur/${firebaseKey}`);
        const updatedData = {
            ...editFormData,
            id_standar_per_umur: firebaseKey,
            last_updated: new Date().toISOString()
        };
        
        await set(dataRef, updatedData);
        
        const updatedList = dataList.map(item => {
            if (item.firebaseKey === firebaseKey) {
                return {
                    ...item,
                    ...updatedData,
                    last_updated: updatedData.last_updated
                };
            }
            return item;
        });
        
        setDataList(updatedList);
        setOriginalDataList(updatedList);
        setEditingRow(null);
        
        setSnackbar({
            open: true,
            message: "Data berhasil diperbarui.",
            severity: "success",
        });
        
        setLastUpdated(new Date());
        
    } catch (error) {
        console.error("Error updating data:", error);
        setSnackbar({
            open: true,
            message: "Gagal memperbarui data.",
            severity: "error",
        });
    }
};

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

const handleDelete = async (firebaseKey, index, umur) => {
    if (window.confirm(`Apakah Anda yakin ingin menghapus data dengan Index: ${index}, Umur: ${umur}?`)) {
        try {
            const dataRef = ref(database, `db_plantation/tb_standar_per_umur/${firebaseKey}`);
            await remove(dataRef);
            
            const updatedList = dataList.filter(item => item.firebaseKey !== firebaseKey);
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            
            // Success handling...
            
        } catch (error) {
            // Error handling...
        }
    }
};

    const handleAddNew = () => {
        setIsAddIconRotated(!isAddIconRotated);
        setShowAddForm(!showAddForm);
    };

    const handleNewDataChange = (field, value) => {
        setNewData({
            ...newData,
            [field]: field === 'index' ? parseIndexValue(value) : 
                    ['setara_urea', 'k_per_n', 'setara_k', 'setara_n', 'setara_k2so4', 'standar_sbt'].includes(field) 
                    ? parseNumericValue(value) : value
        });
    };

const handleSaveNew = async () => {
    const indexNum = parseInt(newData.index);
    if (!newData.index.trim() || isNaN(indexNum)) {
        setSnackbar({
            open: true,
            message: "Index tidak boleh kosong dan harus berupa angka.",
            severity: "warning",
        });
        return;
    }

    if (isDataExists(newData.index)) {
        setSnackbar({
            open: true,
            message: `Data dengan Index "${newData.index}" sudah ada dalam database.`,
            severity: "warning",
        });
        return;
    }

    try {
        const dbPath = "db_plantation/tb_standar_per_umur";
        const newRef = push(ref(database, dbPath));
        
        const dataToSave = {
            ...newData,
            last_updated: new Date().toISOString()
        };
        
        await set(newRef, {
            ...dataToSave,
            id_standar_per_umur: newRef.key
        });
        
        const newItem = {
            firebaseKey: newRef.key,
            id_standar_per_umur: newRef.key,
            ...dataToSave
        };
        
        const updatedList = [...originalDataList, newItem];
        
        setDataList(updatedList);
        setOriginalDataList(updatedList);
        
        // Reset form...
        
    } catch (error) {
        console.error("Error adding data:", error);
        // Error handling...
    }
};

    const handleCancelNew = () => {
        setShowAddForm(false);
        setIsAddIconRotated(false);
        setNewData({
            index: '',
            umur: '',
            jenis_bibit: '',
            kelas_bibit: '',
            setara_urea: '',
            k_per_n: '',
            setara_k: '',
            setara_n: '',
            setara_k2so4: '',
            standar_sbt: ''
        });
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    // Pastikan data sudah terurut berdasarkan index sebelum ditampilkan
    const sortedDataList = [...dataList].sort((a, b) => {
        const indexA = parseInt(a.index) || 0;
        const indexB = parseInt(b.index) || 0;
        
        // Jika index sama, urutkan berdasarkan umur
        if (indexA === indexB) {
            const umurA = parseInt(a.umur) || 0;
            const umurB = parseInt(b.umur) || 0;
            return umurA - umurB;
        }
        
        return indexA - indexB;
    });

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Standar Per Umur
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        {/* IconButton Plus/X untuk Tambah Data */}
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleAddNew}
                            sx={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: isAddIconRotated ? 'rotate(45deg)' : 'rotate(0deg)',
                                backgroundColor: isAddIconRotated ? '#f2f9ff' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isAddIconRotated ? '#f2f9ff' : 'action.hover'
                                }
                            }}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Form Tambah Data Baru - SEMUA INPUT VERTIKAL */}
                {showAddForm && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f2f9ff' }} elevation={0}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Tambah Data Standar Per Umur Baru
                        </Typography>
                        <Stack direction="column" spacing={2}>
                            <TextField
                                label="Index"
                                size="small"
                                value={newData.index}
                                onChange={(e) => handleNewDataChange('index', e.target.value)}
                                required
                                type="number"
                                fullWidth
                                inputProps={{ min: 1 }}
                            />
                            <TextField
                                label="Umur"
                                size="small"
                                value={newData.umur}
                                onChange={(e) => handleNewDataChange('umur', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Jenis Bibit"
                                size="small"
                                value={newData.jenis_bibit}
                                onChange={(e) => handleNewDataChange('jenis_bibit', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Kelas Bibit"
                                size="small"
                                value={newData.kelas_bibit}
                                onChange={(e) => handleNewDataChange('kelas_bibit', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Setara Urea"
                                size="small"
                                value={newData.setara_urea}
                                onChange={(e) => handleNewDataChange('setara_urea', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="K/N"
                                size="small"
                                value={newData.k_per_n}
                                onChange={(e) => handleNewDataChange('k_per_n', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Setara K"
                                size="small"
                                value={newData.setara_k}
                                onChange={(e) => handleNewDataChange('setara_k', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Setara N"
                                size="small"
                                value={newData.setara_n}
                                onChange={(e) => handleNewDataChange('setara_n', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Setara K2SO4"
                                size="small"
                                value={newData.setara_k2so4}
                                onChange={(e) => handleNewDataChange('setara_k2so4', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Standar SBT"
                                size="small"
                                value={newData.standar_sbt}
                                onChange={(e) => handleNewDataChange('standar_sbt', e.target.value)}
                                fullWidth
                            />
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button variant="outlined" onClick={handleCancelNew}>
                                Batal
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={handleSaveNew} 
                                disabled={isDataExists(newData.index)}
                            >
                                Simpan
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="standar per umur table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="ded-tch">Index</TableCell>
                                        <TableCell className="ded-tch">Umur</TableCell>
                                        <TableCell className="ded-tch">Jenis Bibit</TableCell>
                                        <TableCell className="ded-tch">Kelas Bibit</TableCell>
                                        <TableCell className="ded-tch">Setara Urea</TableCell>
                                        <TableCell className="ded-tch">K/N</TableCell>
                                        <TableCell className="ded-tch">Setara K</TableCell>
                                        <TableCell className="ded-tch">Setara N</TableCell>
                                        <TableCell className="ded-tch">Setara K2SO4</TableCell>
                                        <TableCell className="ded-tch">Standar SBT</TableCell>
                                        <TableCell className="ded-tch" style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.firebaseKey}>
                                                {editingRow === row.firebaseKey ? (
                                                    <>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.index}
                                                                onChange={(e) => handleEditChange('index', e.target.value)}
                                                                type="number"
                                                                fullWidth
                                                                inputProps={{ min: 1 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.umur}
                                                                onChange={(e) => handleEditChange('umur', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.jenis_bibit}
                                                                onChange={(e) => handleEditChange('jenis_bibit', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.kelas_bibit}
                                                                onChange={(e) => handleEditChange('kelas_bibit', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.setara_urea}
                                                                onChange={(e) => handleEditChange('setara_urea', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.k_per_n}
                                                                onChange={(e) => handleEditChange('k_per_n', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.setara_k}
                                                                onChange={(e) => handleEditChange('setara_k', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.setara_n}
                                                                onChange={(e) => handleEditChange('setara_n', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.setara_k2so4}
                                                                onChange={(e) => handleEditChange('setara_k2so4', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.standar_sbt}
                                                                onChange={(e) => handleEditChange('standar_sbt', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="success"
                                                                    onClick={() => handleSaveEdit(row.firebaseKey)}
                                                                >
                                                                    <CheckOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    <CloseOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>{formatText(row.index)}</TableCell>
                                                        <TableCell>{formatText(row.umur)}</TableCell>
                                                        <TableCell>{formatText(row.jenis_bibit)}</TableCell>
                                                        <TableCell>{formatText(row.kelas_bibit)}</TableCell>
                                                        <TableCell>{displayRoundedValue(row.setara_urea)}</TableCell>
                                                        <TableCell>{displayRoundedValue(row.k_per_n)}</TableCell>
                                                        <TableCell>{displayRoundedValue(row.setara_k)}</TableCell>
                                                        <TableCell>{displayRoundedValue(row.setara_n)}</TableCell>
                                                        <TableCell>{displayRoundedValue(row.setara_k2so4)}</TableCell>
                                                        <TableCell>{displayRoundedValue(row.standar_sbt)}</TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="primary"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={() => handleDelete(row.firebaseKey, row.index, row.umur)}
                                                                >
                                                                    <DeleteOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data. Klik icon "+" untuk menambahkan atau "Import Excel" untuk mengimpor data.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default StandarPerUmur;